<script setup>
/**
 * The list of things to do — and, when there is nothing to do, the calm sentence that
 * says so.
 *
 * THE EMPTY STATE IS THE GOOD STATE AND MUST NOT LOOK LIKE AN ERROR. An owner whose
 * assistant is healthy sees this card most days; if it reads as a failure, or as a page
 * that did not finish loading, the screen has taught her to distrust it. So: a solid
 * card, a check mark in the accent tile, one sentence in the same voice as everything
 * else. No dashed border (that is dropzone convention), no illustration, no "0".
 *
 * AND IT IS ONLY REACHED WHEN ALL THREE SOURCES ARE KNOWN. "Nothing needs you right now"
 * is a claim about three independent reads. `buildAttentionQueue` names any source it
 * could not check, and this component renders that instead of the all-clear — because a
 * failed read that silently contributed zero rows would put that sentence on a workspace
 * whose only channel is disconnected.
 */
import { computed, nextTick, ref } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';

import AttentionRow from './AttentionRow.vue';
import TeachAnswerDialog from './TeachAnswerDialog.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** Rows from `buildAttentionQueue`, already ranked and capped. */
  rows: { type: Array, default: () => [] },
  /** Source names (`ATTENTION_SOURCE`) that could not be read this time. */
  unchecked: { type: Array, default: () => [] },
  accountId: { type: Number, default: null },
  /**
   * P-5.2: while the assistant practises, the readiness path stands where the calm all-clear
   * would be. "Your assistant is handling its messages on its own" is not true yet.
   */
  practising: { type: Boolean, default: false },
});

/** A row changed something the page reads (P-1.3's "Send them to me", a saved fix). */
const emit = defineEmits(['changed']);

const { t } = useWasloI18n();

/**
 * C3 P-6.4: "Fix it" on an unanswered row. The dialog is mounted HERE, once, outside the
 * list, and holds its own copy of the row. A saved fix makes the page read the queue again,
 * the fixed question leaves `rows` (and the whole list goes with it when it was the last),
 * and the dialog is still on screen: what the save did is said once (P-6.3), and "Check it
 * now" is the proof (P-6.4). Mounted inside the row, it went with the row.
 */
const fixing = ref(null);
const fixRef = ref(null);
const fix = async row => {
  fixing.value = row;
  await nextTick();
  fixRef.value?.open();
};

const uncheckedMessage = computed(() => {
  if (props.unchecked.length === 0) return '';
  const names = props.unchecked
    .map(source =>
      t(`WASLO.OVERVIEW.ATTENTION.SOURCES.${source.toUpperCase()}`)
    )
    .join(t('WASLO.COMMON.LIST_SEPARATOR'));
  return t('WASLO.OVERVIEW.ATTENTION.UNCHECKED', { sources: names });
});

/** The all-clear is a claim, so it is only made when there is nothing left unknown. */
const isAllClear = computed(
  () =>
    !props.practising && props.rows.length === 0 && props.unchecked.length === 0
);
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-attention>
    <Banner
      v-if="uncheckedMessage"
      color="slate"
      data-waslo-attention-unchecked
    >
      {{ uncheckedMessage }}
    </Banner>

    <ul
      v-if="rows.length"
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
      data-waslo-attention-list
    >
      <AttentionRow
        v-for="row in rows"
        :key="row.id"
        :row="row"
        :account-id="accountId"
        @changed="emit('changed')"
        @fix="fix"
      />
    </ul>

    <div
      v-else-if="isAllClear"
      class="flex items-start w-full gap-4 px-6 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
      data-waslo-attention-clear
    >
      <span
        class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-blue-3"
        aria-hidden="true"
      >
        <span class="size-5 i-lucide-check text-n-blue-10" />
      </span>
      <div class="flex flex-col gap-1">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.OVERVIEW.ATTENTION.CLEAR_TITLE') }}
        </p>
        <p class="mb-0 text-sm leading-6 text-n-slate-11">
          {{ t('WASLO.OVERVIEW.ATTENTION.CLEAR_BODY') }}
        </p>
      </div>
    </div>

    <TeachAnswerDialog
      v-if="fixing"
      :key="fixing.id"
      ref="fixRef"
      mode="fix"
      :account-id="accountId"
      :gap-id="fixing.gapId ?? null"
      :suggested-question="fixing.query"
      @saved="emit('changed')"
    />
  </div>
</template>
